import React from 'react'

import { Link, useNavigate } from 'react-router-dom';
import AddItemWizard from './AddItemWizard.jsx';
import EditListingModal from './EditListingModal.jsx';
// Helper function to format prices
function formatPrice(price) {
    if (!price || price === 0) return 'On Request';
    return new Intl.NumberFormat('en-ZA', {
        style: 'currency',
        currency: 'ZAR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(price);
}

// Simple Pagination component
function Pagination({ currentPage, totalPages, onPageChange }) {
    if (totalPages <= 1) return null;

    const getPageItems = () => {
        const pageNeighbours = 1;
        const totalNumbers = (pageNeighbours * 2) + 3;
        const totalBlocks = totalNumbers + 2;

        if (totalPages <= totalBlocks) {
            const pages = [];
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }

        const pages = [];
        const startPage = Math.max(2, currentPage - pageNeighbours);
        const endPage = Math.min(totalPages - 1, currentPage + pageNeighbours);

        pages.push(1);

        if (startPage > 2) {
            pages.push('...');
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }

        if (endPage < totalPages - 1) {
            pages.push('...');
        }

        pages.push(totalPages);
        return pages;
    };

    const pageItems = getPageItems();

    return (
        <nav className="mt-6 flex justify-center relative z-10">
            <div className="inline-flex bg-white rounded-xl shadow-md border border-gray-200 p-2">
                <ul className="flex items-center space-x-1">
                    <li>
                        <button
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-700 hover:bg-blue-600 hover:text-white"
                        >
                            <i className="fas fa-chevron-left mr-1"></i>
                            <span className="hidden sm:inline">Previous</span>
                        </button>
                    </li>
                    {pageItems.map((item, index) => {
                        if (item === '...') {
                            return (
                                <li key={`ellipsis-${index}`}>
                                    <span className="px-3 py-2 text-gray-400">...</span>
                                </li>
                            );
                        }
                        return (
                            <li key={item}>
                                <button
                                    onClick={() => onPageChange(item)}
                                    className={`w-10 h-10 rounded-lg text-sm font-bold transition-colors ${currentPage === item ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-blue-100'}`}
                                >
                                    {item}
                                </button>
                            </li>
                        );
                    })}
                    <li>
                        <button
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 text-gray-700 hover:bg-blue-600 hover:text-white"
                        >
                            <span className="hidden sm:inline">Next</span>
                            <i className="fas fa-chevron-right ml-1"></i>
                        </button>
                    </li>
                </ul>
            </div>
        </nav>
    );
}

// Mock SuspensionModal component (placeholder)
function SuspensionModal({ user, onDocumentsSubmitted, onSwitchRole, onLogout, onUserUpdate }) {
    if (!user || (user.status !== 'suspended' && user.status !== 'pending_review')) {
        return null;
    }
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Account Restricted</h2>
                <p className="text-gray-600 mb-4">Your account is currently under review. Please contact support for assistance.</p>
                <div className="flex justify-end space-x-4">
                    <button onClick={onLogout} className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700">Logout</button>
                </div>
            </div>
        </div>
    );
}

// A reusable component to display star ratings.
// Defined outside the main component to prevent re-creation on every render.
function StarRating({ rating = 0, count = 0 }) {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);

    // If there are no ratings, display a clear message consistent with other stat cards.
    if (count === 0) {
        return <p className="text-3xl font-bold text-gray-400">No Ratings</p>;
    }

    return (
        <div>
            <div className="flex text-amber-400 text-3xl">
                {[...Array(fullStars)].map((_, i) => <i key={`full-${i}`} className="fas fa-star"></i>)}
                {halfStar && <i className="fas fa-star-half-alt"></i>}
                {[...Array(emptyStars)].map((_, i) => <i key={`empty-${i}`} className="far fa-star"></i>)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
                {parseFloat(rating).toFixed(1)} average from {count} {count === 1 ? 'rating' : 'ratings'}
            </p>
        </div>
    );
}

// --- NEW: Modal to prompt for profile completion ---
function ProfileIncompleteModal({ isOpen, onClose, missingFields }) {

    if (!isOpen) return null;

    const handleGoToProfile = () => {
        onClose();
        navigate('/profile');
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Complete Your Profile</h2>
                <p className="text-gray-600 mb-4">To add a new listing, you must first complete your seller profile. This ensures smooth and secure transactions for everyone.</p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <h3 className="font-semibold text-red-800 mb-2">Missing Information:</h3>
                    <ul className="list-disc list-inside text-red-700 space-y-1">
                        {missingFields.map(field => <li key={field}>{field}</li>)}
                    </ul>
                </div>
                <div className="flex justify-end space-x-4">
                    <button onClick={onClose} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={handleGoToProfile} className="primary-gradient text-white py-2 px-4 rounded-lg hover:opacity-90">Go to My Profile</button>
                </div>
            </div>
        </div>
    );
}

function SellerDashboard({ user, onSwitchRole, onLogout, onUserUpdate }) {
    const navigate = useNavigate();
    
    const [myProducts, setMyProducts] = React.useState([]);
    const [drafts, setDrafts] = React.useState([]);
    const [loadingDrafts, setLoadingDrafts] = React.useState(true);
    const [categories, setCategories] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [salesStats, setSalesStats] = React.useState({ salesMade: 0, totalEarnings: 0, avg_rating: 0, rating_count: 0 });
    const [loadingSalesStats, setLoadingSalesStats] = React.useState(true);
    const [soldItems, setSoldItems] = React.useState([]);
    const [loadingSoldItems, setLoadingSoldItems] = React.useState(true);
    const [soldItemsError, setSoldItemsError] = React.useState(null);
    const [actionMessage, setActionMessage] = React.useState(null);

    // --- State for modals ---
    const [showAddItemWizard, setShowAddItemWizard] = React.useState(false);
    const [showEditListingModal, setShowEditListingModal] = React.useState(false);
    const [listingToEdit, setListingToEdit] = React.useState(null);
    const [draftToEdit, setDraftToEdit] = React.useState(null); // NEW: For resuming a draft
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = React.useState(false);
    const [listingToDelete, setListingToDelete] = React.useState(null);
    // --- NEW: State for profile check ---
    const [showProfileIncompleteModal, setShowProfileIncompleteModal] = React.useState(false);
    const [missingProfileFields, setMissingProfileFields] = React.useState([]);
    const [isCheckingProfile, setIsCheckingProfile] = React.useState(false);

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    // --- Pagination State ---
    const [myProductsPage, setMyProductsPage] = React.useState(1);
    const [draftsPage, setDraftsPage] = React.useState(1);
    const [soldItemsPage, setSoldItemsPage] = React.useState(1);
    const itemsPerPage = 5;

    // --- NEW: State for My Listings search and filter ---
    const [myProductsSearchTerm, setMyProductsSearchTerm] = React.useState('');
    const [myProductsCategoryFilter, setMyProductsCategoryFilter] = React.useState('all');
    const [myProductsSubCategoryFilter, setMyProductsSubCategoryFilter] = React.useState('all');

    const stats = React.useMemo(() => {
        const allListings = [...myProducts, ...drafts];
        if (allListings.length === 0) {
            return { total: 0, approved: 0, pending: 0, rejected: 0, drafts: 0, listingsValue: 0 };
        }
        return {
            total: allListings.length,
            approved: allListings.filter(p => p.status === 'approved').length,
            pending: allListings.filter(p => p.status === 'pending').length,
            rejected: allListings.filter(p => p.status === 'rejected').length,
            drafts: allListings.filter(p => p.status === 'draft' || (p.listing_number && p.listing_number.startsWith('DRAFT-'))).length,
            listingsValue: allListings.reduce((sum, p) => sum + (parseFloat(p.price) || 0), 0),
        };
    }, [myProducts, drafts]);

    // --- REFACTORED: Fetches all listing types for the seller ---
    const fetchMyProducts = React.useCallback(async () => {
        try {
            setLoading(true);
            setLoadingDrafts(true);
            // Use the new universal endpoint
            const response = await fetch(`/api/get_seller_listings.php?seller_id=${user.id}`, {
                credentials: 'include'
            });
            if (response.status === 401 || response.status === 403) {
                onLogout();
                return;
            }
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            // NEW: Filter listings by status OR listing number convention
            // This ensures drafts appear in the correct section even if the DB status column is restricted (e.g. ENUM)
            setMyProducts(data.filter(p => p.status !== 'draft' && (!p.listing_number || !p.listing_number.startsWith('DRAFT-'))));
            setDrafts(data.filter(p => p.status === 'draft' || (p.listing_number && p.listing_number.startsWith('DRAFT-'))));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setLoadingDrafts(false);
        }
    }, [user.id]);

    const fetchSoldItems = React.useCallback(async () => {
        if (!user || !user.id) return;
        try {
            setLoadingSoldItems(true);
            const response = await fetch(`/api/seller_quote_requests.php?seller_id=${user.id}`, {
                credentials: 'include'
            });
            if (response.status === 401 || response.status === 403) {
                onLogout();
                return;
            }
            if (!response.ok) throw new Error('Could not fetch quote requests.');
            const data = await response.json();
            setSoldItems(data);
        } catch (err) {
            setSoldItemsError(err.message);
        } finally {
            setLoadingSoldItems(false);
        }
    }, [user.id]);

    const fetchSalesStats = React.useCallback(async () => {
        if (!user || !user.id) return;

        try {
            setLoadingSalesStats(true);
            const response = await fetch(`/api/seller_stats.php?seller_id=${user.id}`, {
                credentials: 'include'
            });
            if (response.status === 401 || response.status === 403) {
                onLogout();
                return;
            }
            if (!response.ok) {
                throw new Error('Could not fetch sales stats.');
            }
            const data = await response.json();
            setSalesStats(data);
        } catch (err) {
            setError(prev => prev ? `${prev} | ${err.message}` : err.message);
        } finally {
            setLoadingSalesStats(false);
        }
    }, [user.id]);

    const handleListingUpdated = () => {
        fetchMyProducts();
        // Also refetch stats in case a price was updated
        fetchSalesStats();
    };

    const fetchCategories = React.useCallback(async () => {
        try {
            const response = await fetch(`/api/categories.php?t=${new Date().getTime()}`, {
                credentials: 'include'
            });
            if (response.status === 401 || response.status === 403) {
                onLogout();
                return;
            }
            if (!response.ok) throw new Error('Could not fetch categories.');
            const data = await response.json();
            setCategories(data);
        } catch (err) {
            setError(prev => prev ? `${prev} | ${err.message}` : err.message);
        }
    }, []);

    React.useEffect(() => {
        fetchMyProducts();
        fetchSalesStats();
        fetchSoldItems();
        fetchCategories();
    }, [fetchMyProducts, fetchSalesStats, fetchSoldItems, fetchCategories]);

    // --- NEW: Handler for the "Add New Item" button click ---
    const handleAddNewItemClick = async () => {
        setIsCheckingProfile(true);
        setActionMessage(null); // Clear previous messages
        try {
            const response = await fetch('/api/check_profile_status.php', {
                credentials: 'include'
            });
            if (response.status === 401 || response.status === 403) {
                onLogout();
                return;
            }
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.error || 'Could not verify profile status.');
            }

            if (result.isComplete) {
                setShowAddItemWizard(true);
            } else {
                setMissingProfileFields(result.missingFields || []);
                setShowProfileIncompleteModal(true);
            }
        } catch (err) {
            setActionMessage({ type: 'error', text: err.message });
        } finally {
            setIsCheckingProfile(false);
        }
    };

    // --- NEW: Listen for custom event to open AddItemWizard from bottom navigation ---
    React.useEffect(() => {
        const handleOpenAddItemWizard = () => {
            handleAddNewItemClick();
        };

        window.addEventListener('openAddItemWizard', handleOpenAddItemWizard);
        
        return () => {
            window.removeEventListener('openAddItemWizard', handleOpenAddItemWizard);
        };
    }, [handleAddNewItemClick]);

    // --- NEW: Handle anchor scrolling for bottom navigation ---
    React.useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash;
            if (hash) {
                // Remove the # and find the element
                const elementId = hash.substring(1);
                const element = document.getElementById(elementId);
                
                if (element) {
                    // Small delay to ensure content is rendered
                    setTimeout(() => {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                }
            }
        };

        // Handle initial hash and future changes
        handleHashChange();
        
        // Listen for hash changes
        window.addEventListener('hashchange', handleHashChange);
        
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []); // Run once when component mounts

    // --- NEW: Handlers for the universal Edit and Delete modals ---
    const handleEditClick = (product) => {
        setListingToEdit(product);
        setShowEditListingModal(true);
    };

    // NEW: Handler to open the wizard with a draft
    const handleResumeDraft = (draft) => {
        setDraftToEdit(draft);
        setShowAddItemWizard(true);
    };

    const handleCloseWizard = () => {
        setShowAddItemWizard(false);
        setDraftToEdit(null); // Clear the draft being edited
    };

    const handleDeleteClick = (product) => {
        setListingToDelete(product);
        setShowDeleteConfirmModal(true);
    };

    const confirmDelete = async () => {
        if (!listingToDelete) return;
        setIsSubmitting(true);
        setActionMessage(null);

        try {
            const response = await fetch('/api/delete_listing.php', {
                method: 'POST', // Using POST for simplicity
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: listingToDelete.id,
                    listingType: listingToDelete.listingType
                })
            });
            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || 'Failed to delete listing.');
            }
            setActionMessage({ type: 'success', text: result.message });
            fetchMyProducts(); // Refresh the list
            setShowDeleteConfirmModal(false);
            setListingToDelete(null);
        } catch (err) {
            setActionMessage({ type: 'error', text: err.message });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleListingAdded = () => {
        fetchMyProducts(); // Re-fetch listings to reflect status change
    };

    const handleStartDelivery = async (orderId, allocationId) => {
        setActionMessage(null);
        setSoldItems(prevItems => prevItems.map(item =>
            item.id === orderId ? { ...item, isProcessing: true } : item
        ));

        try {
            const response = await fetch('/api/start_delivery.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ order_id: orderId, allocation_id: allocationId })
            });
            const result = await response.json();
            if (!response.ok) throw new Error(result.error || 'Failed to start delivery.');

            setActionMessage({ type: 'success', text: result.message });
            fetchSoldItems();
        } catch (err) {
            setActionMessage({ type: 'error', text: err.message });
        }
    };

    // --- UPDATED: Filtering logic for My Listings with two-level dropdowns ---
    const filteredMyProducts = React.useMemo(() => {
        let filtered = myProducts;

        // Filter by category
        const mainCatId = parseInt(myProductsCategoryFilter, 10);
        const subCatId = parseInt(myProductsSubCategoryFilter, 10);

        if (!isNaN(mainCatId)) { // A main category is selected (not 'all')
            if (!isNaN(subCatId)) {
                // A specific sub-category is selected, so filter by its ID only.
                filtered = filtered.filter(p => p.category_id === subCatId);
            } else {
                // "All Sub-categories" is selected for a main category.
                // Get all sub-category IDs for the selected main category.
                // FIX: Ensure parent_id is parsed to an integer for correct comparison.
                const subCategoryIds = categories
                    .filter(cat => parseInt(cat.parent_id, 10) === mainCatId)
                    .map(cat => cat.id);
                
                // The target list must include the main category itself plus all its children.
                const targetCategoryIds = [mainCatId, ...subCategoryIds];
                filtered = filtered.filter(p => targetCategoryIds.includes(p.category_id));
            }
        }

        // Filter by search term
        if (myProductsSearchTerm.trim() !== '') {
            const searchTermLower = myProductsSearchTerm.toLowerCase();
            filtered = filtered.filter(p =>
                p.name.toLowerCase().includes(searchTermLower) ||
                (p.listing_number && p.listing_number.toLowerCase().includes(searchTermLower))
            );
        }
        return filtered;
    }, [myProducts, myProductsSearchTerm, myProductsCategoryFilter, myProductsSubCategoryFilter, categories]);

    // --- NEW: Memoized list of sub-categories for the second dropdown ---
    const subCategoryOptions = React.useMemo(() => {
        if (myProductsCategoryFilter === 'all' || !categories) {
            return [];
        }
        const mainCatId = parseInt(myProductsCategoryFilter, 10);
        return categories.filter(cat => parseInt(cat.parent_id, 10) === mainCatId);
    }, [myProductsCategoryFilter, categories]);

    // --- Pagination Logic ---
    const totalMyProductsPages = Math.ceil(filteredMyProducts.length / itemsPerPage);
    const currentProducts = filteredMyProducts.slice((myProductsPage - 1) * itemsPerPage, myProductsPage * itemsPerPage);
    const totalDraftsPages = Math.ceil(drafts.length / itemsPerPage);
    const currentDrafts = drafts.slice((draftsPage - 1) * itemsPerPage, draftsPage * itemsPerPage);
    const totalSoldItemsPages = Math.ceil(soldItems.length / itemsPerPage);
    const currentSoldItems = soldItems.slice((soldItemsPage - 1) * itemsPerPage, soldItemsPage * itemsPerPage);

    // --- NEW: Check for suspension ---
    const isAccountRestricted = (user.status === 'suspended' || user.status === 'pending_review') && user.suspension_details;

    const handleDocumentsSubmitted = () => {
        // A simple way to refresh the user's state is to reload the page.
        // A more advanced implementation might use a global state management library.
        window.location.reload();
    };

    return (
        <div data-name="seller-dashboard" data-file="components/SellerDashboard.js" className="min-h-screen py-8">
            <div className="container mx-auto px-4">
                <div id="dashboard" className="mb-10">
                    <h1 className="text-4xl md:text-6xl font-black text-white tracking-tighter uppercase">Seller Dashboard</h1>
                    <p className="text-blue-400 font-bold uppercase tracking-[0.3em] text-xs mt-2">Inventory Management & Sales Performance</p>
                    
                    {/* Role Switching and Profile Buttons */}
                    <div className="flex flex-wrap gap-3 mt-6">
                        {/* Profile Button */}
                        <button 
                            onClick={() => navigate('/profile')}
                            className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors text-sm flex items-center backdrop-blur-sm border border-white/20"
                        >
                            <i className="fas fa-user-circle mr-2"></i>
                            My Profile
                        </button>
                        
                        {/* Role Switch Buttons */}
                        {user && user.roles && user.roles.length > 1 && (
                            <>
                                {/* Buyer Button */}
                                <button 
                                    onClick={() => user.activeRole !== 'buyer' && onSwitchRole('buyer')}
                                    className={`px-4 py-2 rounded-lg transition-colors text-sm flex items-center backdrop-blur-sm border ${
                                        user.activeRole === 'buyer' 
                                            ? 'bg-blue-600/80 hover:bg-blue-700/80 text-white border-blue-400/30' 
                                            : 'bg-gray-500/30 hover:bg-gray-500/40 text-gray-400 border-gray-500/20'
                                    }`}
                                    disabled={user.activeRole === 'buyer'}
                                >
                                    <i className="fas fa-shopping-cart mr-2"></i>
                                    Buyer
                                </button>
                                
                                {/* Seller Button (Active) */}
                                <button 
                                    onClick={() => user.activeRole !== 'seller' && onSwitchRole('seller')}
                                    className={`px-4 py-2 rounded-lg transition-colors text-sm flex items-center backdrop-blur-sm border ${
                                        user.activeRole === 'seller' 
                                            ? 'bg-blue-600/80 hover:bg-blue-700/80 text-white border-blue-400/30' 
                                            : 'bg-gray-500/30 hover:bg-gray-500/40 text-gray-400 border-gray-500/20 cursor-not-allowed'
                                    }`}
                                    disabled={user.activeRole === 'seller'}
                                >
                                    <i className="fas fa-store mr-2"></i>
                                    Seller
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* --- Anchor Navigation Menu --- */}
                <nav className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-sm rounded-xl mb-8 border border-white/10">
                    <div className="flex items-center gap-1 p-2 overflow-x-auto">
                        <a
                            href="#dashboard"
                            onClick={(e) => { e.preventDefault(); document.getElementById('dashboard')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white/70 hover:text-white hover:bg-white/10 transition-colors whitespace-nowrap"
                        >
                            <i className="fas fa-tachometer-alt text-xs"></i>
                            Dashboard
                        </a>
                        <a
                            href="#listings"
                            onClick={(e) => { e.preventDefault(); document.getElementById('listings')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white/70 hover:text-white hover:bg-white/10 transition-colors whitespace-nowrap"
                        >
                            <i className="fas fa-box-open text-xs"></i>
                            My Listings
                        </a>
                        <a
                            href="#orders"
                            onClick={(e) => { e.preventDefault(); document.getElementById('orders')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white/70 hover:text-white hover:bg-white/10 transition-colors whitespace-nowrap"
                        >
                            <i className="fas fa-truck text-xs"></i>
                            Orders
                        </a>
                        <a
                            href="#analytics"
                            onClick={(e) => { e.preventDefault(); document.getElementById('analytics')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white/70 hover:text-white hover:bg-white/10 transition-colors whitespace-nowrap"
                        >
                            <i className="fas fa-chart-bar text-xs"></i>
                            Analytics
                        </a>
                    </div>
                </nav>

                {/* --- UPDATED: Render suspension modal if account is restricted --- */}
                <SuspensionModal 
                    user={user} 
                    onDocumentsSubmitted={handleDocumentsSubmitted} 
                    onSwitchRole={onSwitchRole}
                    onLogout={onLogout}
                    onUserUpdate={onUserUpdate}
                />

                {/* Stats Cards - Analytics Section */}
                <div id="analytics" className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Total Listings</h3>
                        <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Sales Made</h3>
                        {loadingSalesStats ? <i className="fas fa-spinner fa-spin text-xl text-blue-400"></i> : <p className="text-3xl font-bold text-blue-600">{salesStats.salesMade}</p>}
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Total Earnings</h3>
                        {loadingSalesStats ? <i className="fas fa-spinner fa-spin text-xl text-blue-400"></i> : <p className="text-3xl font-bold text-green-600">{formatPrice(salesStats.totalEarnings)}</p>}
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Listings Value</h3>
                        <p className="text-3xl font-bold text-green-600">{formatPrice(stats.listingsValue)}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Approved</h3>
                        <p className="text-3xl font-bold text-green-600">{stats.approved}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Pending</h3>
                        <p className="text-3xl font-bold text-amber-600">{stats.pending}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Rejected</h3>
                        <p className="text-3xl font-bold text-red-600">{stats.rejected}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">Drafts</h3>
                        <p className="text-3xl font-bold text-gray-600">{stats.drafts}</p>
                    </div>
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-2">My Rating</h3>
                        {loadingSalesStats ? <i className="fas fa-spinner fa-spin text-xl text-blue-400"></i> : <StarRating rating={salesStats.avg_rating} count={salesStats.rating_count} />}
                    </div>
                </div>

                {/* Action Message Display */}
                {actionMessage && (
                    <div className={`p-3 rounded-lg text-sm mb-4 ${actionMessage.type === 'error' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                        {actionMessage.text}
                    </div>
                )}

                {/* Sold Items Section */}
                <div id="orders" className={`bg-white rounded-xl shadow-lg p-6 mt-8 ${isAccountRestricted ? 'filter blur-sm pointer-events-none' : ''}`}>
                    <h2 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Quotes Received</h2>
                    {loadingSoldItems && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                    {soldItemsError && <div className="text-red-500 text-center py-4">Error: {soldItemsError}</div>}
                    {!loadingSoldItems && !soldItemsError && soldItems.length === 0 && <p className="text-gray-500 text-center">You have no quote requests yet.</p>}
                    {!loadingSoldItems && !soldItemsError && soldItems.length > 0 && (
                        <div className="space-y-4">
                            {/* Desktop Headers */}
                            <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-2 bg-gray-50 rounded-lg">
                                <div className="md:col-span-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Product</div>
                                <div className="md:col-span-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Date Requested</div>
                                <div className="md:col-span-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Type</div>
                                <div className="md:col-span-1 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Status</div>
                            </div>
                            {/* Quote Requests List */}
                            {currentSoldItems.map(quote => (
                                <div key={quote.request_id} className="bg-white border rounded-lg p-4 grid grid-cols-2 md:grid-cols-12 gap-x-4 gap-y-2 items-center">
                                    {/* Product Info */}
                                    <div className="col-span-2 md:col-span-6">
                                        <div className="text-sm font-medium text-gray-900">{quote.product_name}</div>
                                        <div className="text-sm text-gray-500 font-mono">{quote.listing_number}</div>
                                    </div>
                                    {/* Date Requested */}
                                    <div className="md:col-span-3">
                                        <div className="text-xs text-gray-500 md:hidden">Date Requested</div>
                                        <div className="text-sm text-gray-500">{new Date(quote.request_date).toLocaleDateString()}</div>
                                    </div>
                                    {/* Listing Type */}
                                    <div className="md:col-span-2">
                                        <div className="text-xs text-gray-500 md:hidden">Type</div>
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize bg-blue-100 text-blue-800">
                                            {quote.listing_type}
                                        </span>
                                    </div>
                                    {/* Status */}
                                    <div className="col-span-2 md:col-span-1 text-right">
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize bg-green-100 text-green-800">
                                            New
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <Pagination currentPage={soldItemsPage} totalPages={totalSoldItemsPages} onPageChange={setSoldItemsPage} />
                </div>

                {/* --- NEW: My Drafts Section --- */}
                <div id="my-drafts" className={`bg-white rounded-xl shadow-lg p-6 mt-8 ${isAccountRestricted ? 'filter blur-sm pointer-events-none' : ''}`}>
                    <h2 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em] mb-6">Unpublished Drafts</h2>
                    {loadingDrafts && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                    {!loadingDrafts && drafts.length === 0 && <p className="text-gray-500 text-center">You have no saved drafts.</p>}
                    {!loadingDrafts && drafts.length > 0 && (
                        <div className="space-y-4">
                            {/* Desktop Headers */}
                            <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-2 bg-gray-50 rounded-lg">
                                <div className="md:col-span-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">Listing Info</div>
                                <div className="md:col-span-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Last Saved</div>
                                <div className="md:col-span-3 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions</div>
                            </div>
                            {/* Drafts List */}
                            {currentDrafts.map(draft => (
                                <div key={`${draft.listingType}-${draft.id}`} className="bg-white border rounded-lg p-4 grid grid-cols-2 md:grid-cols-12 gap-x-4 gap-y-2 items-center">
                                    <div className="col-span-2 md:col-span-6">
                                        <div className="text-sm font-medium text-gray-900">{draft.name || '[No Name]'}</div>
                                        <div className="text-sm text-gray-500 capitalize">{draft.listingType}</div>
                                    </div>
                                    <div className="md:col-span-3">
                                        <div className="text-xs text-gray-500 md:hidden">Last Saved</div>
                                        <div className="text-sm text-gray-500">{new Date(draft.updated_at).toLocaleString()}</div>
                                    </div>
                                    <div className="col-span-2 md:col-span-3 flex justify-end items-center space-x-4">
                                        <button onClick={() => handleResumeDraft(draft)} className="text-blue-600 hover:text-blue-900 text-sm font-medium">Continue Editing</button>
                                        <button onClick={() => handleDeleteClick(draft)} className="text-red-600 hover:text-red-900 text-sm font-medium">Delete</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <Pagination currentPage={draftsPage} totalPages={totalDraftsPages} onPageChange={setDraftsPage} />
                </div>


                {/* My Product Listings Section */}
                <div id="listings" className={`bg-white rounded-xl shadow-lg p-6 mt-8 ${isAccountRestricted ? 'filter blur-sm pointer-events-none' : ''}`}>
                    <div className="flex flex-col md:flex-row justify-between md:items-center mb-6 gap-4">
                        <h2 className="text-sm font-black text-gray-400 uppercase tracking-[0.2em] flex-shrink-0">Live Inventory</h2>
                        <div className="flex flex-col lg:flex-row gap-4 w-full lg:w-auto lg:items-center">
                            {/* --- NEW: Search Input --- */}
                            <div className="relative flex-grow">
                                <input
                                    type="text"
                                    placeholder="Search my listings..."
                                    value={myProductsSearchTerm}
                                    onChange={(e) => setMyProductsSearchTerm(e.target.value)}
                                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full"
                                />
                                <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                            </div>
                            {/* --- NEW: Category Filter Dropdown --- */}
                            <select
                                aria-label="Filter by main category"
                                value={myProductsCategoryFilter}
                                onChange={(e) => {
                                    setMyProductsCategoryFilter(e.target.value);
                                    setMyProductsSubCategoryFilter('all'); // Reset sub-category when main changes
                                }}
                                className="border border-gray-300 rounded-lg py-2 px-3"
                            >
                                <option value="all">All Categories</option>
                                {categories.filter(c => !c.parent_id).map(cat => (
                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                ))}
                            </select>
                            {/* --- NEW: Sub-category Filter Dropdown (conditional) --- */}
                            {subCategoryOptions.length > 0 && (
                                <select
                                    aria-label="Filter by sub-category"
                                    value={myProductsSubCategoryFilter}
                                    onChange={(e) => setMyProductsSubCategoryFilter(e.target.value)}
                                    className="border border-gray-300 rounded-lg py-2 px-3"
                                >
                                    <option value="all">All Sub-categories</option>
                                    {subCategoryOptions.map(subCat => (
                                        <option key={subCat.id} value={subCat.id}>{subCat.name}</option>
                                    ))}
                                </select>
                            )}
                            <button
                                onClick={handleAddNewItemClick}
                                disabled={isCheckingProfile}
                                className="primary-gradient text-white font-bold py-2 px-4 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex-shrink-0"
                            >
                                {isCheckingProfile ? <><i className="fas fa-spinner fa-spin mr-2"></i></> : <><i className="fas fa-plus mr-2"></i> Add New Item</>}
                            </button>
                        </div>
                    </div>
                        {loading && <div className="text-center py-4"><i className="fas fa-spinner fa-spin text-2xl text-blue-400"></i></div>}
                        {error && <div className="text-red-500 text-center py-4">Error: {error}</div>}
                        {!loading && !error && filteredMyProducts.length === 0 && <p className="text-gray-500 text-center py-8">No listings match your criteria.</p>}
                        <div className="space-y-4" style={{ display: (!loading && !error && filteredMyProducts.length > 0) ? 'block' : 'none' }}>
                                {/* Desktop Headers - Hidden on mobile */}
                                <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-2 bg-gray-50 rounded-lg">
                                    <div className="md:col-span-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Listing</div>
                                    <div className="md:col-span-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Price</div>
                                    <div className="md:col-span-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Listing #</div>
                                    <div className="md:col-span-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</div>
                                    <div className="md:col-span-2 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions</div>
                                </div>

                                {/* Listings List */}
                                {currentProducts.map(product => (
                                    <div key={product.listingType + '-' + product.id} className="bg-white border rounded-lg p-4 grid grid-cols-2 md:grid-cols-12 gap-x-4 gap-y-2 items-center">
                                        {/* Product Info */}
                                        <div className="col-span-2 md:col-span-4 flex items-center">
                                            {(() => {
                                                let thumb = product.image_url_1;
                                                if (!thumb && product.media && Array.isArray(product.media)) {
                                                    // Find first item with media_type 'image'
                                                    const imgItem = product.media.find(m => m.media_type === 'image');
                                                    if (imgItem) thumb = imgItem.file_path;
                                                }
                                                return null; // No image displayed
                                            })()}
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                                <div className="text-sm text-gray-500">{product.category_name}</div>
                                            </div>
                                        </div>
                                        {/* Price */}
                                        <div className="md:col-span-2">
                                            <div className="text-xs text-gray-500 md:hidden">Price</div>
                                            <div className="text-sm text-gray-900 font-semibold">{product.price ? formatPrice(product.price) : 'On Request'}</div>
                                        </div>
                                        {/* Listing # */}
                                        <div className="md:col-span-2">
                                            <div className="text-xs text-gray-500 md:hidden">Listing #</div>
                                            <div className="text-sm text-gray-500 font-mono">{product.listing_number}</div>
                                        </div>
                                        {/* Status */}
                                        <div className="md:col-span-2">
                                            <div className="text-xs text-gray-500 md:hidden">Status</div>
                                            <span className={
                                                "px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize " +
                                                (product.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                product.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                product.status === 'rejected' ? 'bg-red-100 text-red-800' :
                                                product.status === 'sold' ? 'bg-purple-100 text-purple-800' :
                                                product.status === 'sale_in_progress' ? 'bg-blue-100 text-blue-800' :
                                                'bg-gray-100 text-gray-800')
                                            }>
                                                {product.status.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                        {/* Actions */}
                                        <div className="col-span-2 md:col-span-2 flex justify-end items-center space-x-4">
                                            <button onClick={() => handleEditClick(product)} className="text-blue-600 hover:text-blue-900 text-sm font-medium">Edit</button>
                                            <button onClick={() => handleDeleteClick(product)} className="text-red-600 hover:text-red-900 text-sm font-medium">Delete</button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                    <Pagination currentPage={myProductsPage} totalPages={totalMyProductsPages} onPageChange={setMyProductsPage} />
                </div>

                {/* --- MODALS --- */}
                {/* --- NEW: Profile Incomplete Modal */}
                <ProfileIncompleteModal
                    isOpen={showProfileIncompleteModal}
                    onClose={() => setShowProfileIncompleteModal(false)}
                    missingFields={missingProfileFields}
                />

                <AddItemWizard
                isOpen={showAddItemWizard || !!draftToEdit}
                onClose={handleCloseWizard}
                categories={categories}
                user={user}
                onListingAdded={handleListingUpdated}
                draft={draftToEdit}
            />

            {/* --- NEW: Universal Edit Modal */}
            {showEditListingModal && (
                <EditListingModal
                    isOpen={showEditListingModal}
                    onClose={() => setShowEditListingModal(false)}
                    listing={listingToEdit}
                    onListingUpdated={handleListingUpdated}
                    categories={categories}
                />
            )}

            {/* --- NEW: Universal Delete Modal */}
            {showDeleteConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-8 max-w-md w-full">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Confirm Deletion</h2>
                        <p className="text-gray-600 mb-6">Are you sure you want to delete "<strong>{listingToDelete?.name}</strong>"? This action cannot be undone.</p>
                        {actionMessage && actionMessage.type === 'error' && <div className="text-red-500 text-sm mb-4">{actionMessage.text}</div>}
                        <div className="flex justify-end space-x-4">
                            <button onClick={() => setShowDeleteConfirmModal(false)} className="bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300">Cancel</button>
                            <button onClick={confirmDelete} disabled={isSubmitting} className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50">
                                {isSubmitting ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </div>
    );
}

export default SellerDashboard;
